/**
 * DOM-tree sanitizer — the live-tree companion to the pure-string
 * `content/sanitizer/HTMLSanitizer`. Used where a whole fragment of
 * untrusted HTML becomes live DOM: today the snapshot viewer, which mounts
 * server-fetched snapshot HTML in the editor realm (PM session JWT).
 *
 * POLICY NOTE — this deliberately diverges from the inline `SAFE_TAGS`
 * allowlist (see PHASES.md Phase 4): a snapshot is a full captured page, so
 * structural elements (`div`/`p`/`h1`/`nav`/`table`/…) must survive for the
 * page to render at all. What is enforced instead:
 *   - executable/embedding elements are REMOVED outright
 *   - every `on*` handler attribute (and `srcdoc`) is dropped
 *   - URL-bearing attributes must pass `sanitizeUrl`
 *   - inline `style` keeps any property (layout fidelity) but value-level
 *     dangers are dropped via `sanitizeCssDeclarations`
 *
 * CONTINUOUS DEVELOPMENT: like SAFE_STYLE_PROPS, these sets grow (or
 * tighten) deliberately as real snapshots surface gaps — extend REMOVE_TAGS
 * rather than trusting a new embedding surface, add hostile cases to
 * sanitize-element.test.ts, and keep the WP/edge ports in sync if they ever
 * grow a snapshot feature.
 */

import {
  sanitizeUrl,
  sanitizeCssDeclarations,
} from '../content/sanitizer/HTMLSanitizer.js';

/** Elements that can execute, embed, or retarget — removed with their subtree. */
const REMOVE_TAGS = new Set([
  'script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'base', 'meta', 'link', 'template', 'portal',
]);

/** Attributes whose values navigate or fetch — must pass sanitizeUrl. */
const URL_ATTRS = new Set([
  'href', 'src', 'action', 'formaction', 'poster', 'data', 'xlink:href',
]);

/**
 * Sanitize a live element tree in place (root included).
 */
export function sanitizeElement(root: Element): void {
  // Snapshot the list first — removals during iteration are safe this way.
  const all = [root, ...Array.from(root.querySelectorAll('*'))];
  for (const el of all) {
    const tag = el.tagName.toLowerCase();
    if (REMOVE_TAGS.has(tag)) {
      el.remove();
      continue;
    }
    for (const name of el.getAttributeNames()) {
      const lower = name.toLowerCase();
      if (lower.startsWith('on') || lower === 'srcdoc') {
        el.removeAttribute(name);
        continue;
      }
      if (URL_ATTRS.has(lower)) {
        if (sanitizeUrl(el.getAttribute(name) ?? '') === null) {
          el.removeAttribute(name);
        }
        continue;
      }
      if (lower === 'style') {
        const clean = sanitizeCssDeclarations(el.getAttribute(name) ?? '');
        if (clean === null) el.removeAttribute(name);
        else el.setAttribute(name, clean);
      }
    }
  }
}

/**
 * Best-effort sanitization of a copied `<style>` sheet's text: strips
 * `@import` (arbitrary stylesheet pull-in), neutralizes `url(...)`
 * references that resolve cross-origin (exfil beacons in a tampered
 * snapshot), and defuses legacy `expression(`. Same-origin, relative, and
 * `data:` URLs are kept so the snapshot still renders its images/fonts.
 * The primary defenses are element removal + attribute scrubbing in
 * `sanitizeElement`; this closes the stylesheet side channel.
 */
export function sanitizeStyleSheetText(css: string, baseHref: string): string {
  let out = css.replace(/@import[^;]*(;|$)/gi, '');
  out = out.replace(
    /url\(\s*(['"]?)([^'")]*)\1\s*\)/gi,
    (full, _quote: string, target: string) => {
      try {
        const resolved = new URL(target, baseHref);
        if (resolved.protocol === 'data:') return full;
        if (resolved.origin === new URL(baseHref).origin) return full;
      } catch {
        /* unparseable target → neutralize */
      }
      return 'url("")';
    },
  );
  return out.replace(/expression\s*\(/gi, 'blocked(');
}
